/**
 * PRD.253 §9 / §14 — Need Help support bundle.
 *
 * Builds a diagnostic bundle from the current sync state + pending PowerSync
 * CRUD queue and submits it to the per-project `needHelp` Cloud Function.
 *
 * Privacy: the bundle DOES contain pending operation values (which may include
 * healthcare data) because support needs them to replay stuck writes. The
 * bundle is transmitted only to the project's own authenticated endpoint and
 * stored in Firestore. The notification summary sent onward to Slack is built
 * server-side and never includes operation values (see backend needHelpSlack).
 */

import type { AbstractPowerSyncDatabase } from '@powersync/web';
import {
  getRecentUploadErrors,
  getRetryLogs,
  type LocalUploadErrorEntry,
  type SyncHealthState,
  type SyncRetryLogEntry,
} from './syncHealth';

// ─── Bundle shapes (mirror apps/syncWrite/src/needHelpTypes.ts) ───

export type AppName = 'web-admin' | 'mobile-doctor' | 'mobile-patient';

export interface SyncPendingOperation {
  opId: string;
  table: string;
  type: 'PUT' | 'PATCH' | 'DELETE' | string;
  primaryKey: Record<string, unknown>;
  values?: Record<string, unknown>;
}

export interface SyncNeedHelpManifest {
  caseId: string;
  createdAt: string;
  appName: string;
  pendingCount: number;
  firebaseProjectId?: string;
  orgSlug?: string;
  userUid?: string | null;
  userEmail?: string | null;
  lastErrorMessage?: string | null;
}

export interface SyncNeedHelpBundle {
  caseId: string;
  createdAt: string;

  app: {
    name: AppName;
    version?: string;
    environment?: string;
  };

  user: {
    uid?: string | null;
    email?: string | null;
  };

  project: {
    firebaseProjectId?: string;
    orgSlug?: string;
    clinicName?: string;
  };

  sync: {
    state: SyncHealthState;
    lastSyncedAt?: string | null;
    pendingCount: number;
    estimatedSizeBytes?: number;
    writeUrl?: string;
    lastErrorMessage?: string | null;
  };

  pendingOperations: SyncPendingOperation[];
  recentUploadErrors: LocalUploadErrorEntry[];
  retryLogs?: SyncRetryLogEntry[];

  environment: {
    userAgent?: string;
    platform?: string;
    language?: string;
    timezone?: string;
    online?: boolean;
    currentUrl?: string;
  };
}

export interface SyncNeedHelpRequest {
  caseId: string;
  manifest: SyncNeedHelpManifest;
  bundleBase64: string;
  bundleContentType: 'application/json';
}

export interface SubmitResult {
  ok: boolean;
  caseId: string;
}

// ─── Case ID ───

/**
 * Generate a client-side case ID like `SYNC-2026-7Q3F9K`.
 *
 * The server treats this as a suggestion and may reconcile collisions; what
 * matters is that it is human-quotable and unique enough to correlate.
 */
export function generateCaseId(now: Date = new Date()): string {
  const year = now.getFullYear();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  const time = now.getTime().toString(36).slice(-4).toUpperCase();
  return `SYNC-${year}-${time}${rand}`;
}

// ─── Pending operation extraction ───

interface RawCrudEntry {
  clientId?: number;
  id: string;
  op: string;
  table: string;
  opData?: Record<string, unknown> | null;
}

/**
 * Extract pending PowerSync CRUD entries into the normalized bundle shape.
 *
 * Uses `getCrudBatch`, which is non-destructive — it does NOT complete or
 * remove the operations from the local queue.
 */
export async function extractPendingOperations(
  db: AbstractPowerSyncDatabase,
  limit = 10_000,
): Promise<SyncPendingOperation[]> {
  // getCrudBatch is the supported way to read the pending queue without
  // mutating it. We never call batch.complete().
  const batch = await db.getCrudBatch(limit);
  if (!batch) return [];

  return batch.crud.map((entry) => {
    const e = entry as unknown as RawCrudEntry;
    return {
      opId: String(e.clientId ?? ''),
      table: e.table,
      type: e.op,
      primaryKey: { id: e.id },
      values: e.opData ?? undefined,
    };
  });
}

// ─── Bundle build ───

export interface BuildBundleParams {
  caseId: string;
  state: SyncHealthState;
  lastSyncedAt?: string | null;
  pendingCount: number;
  estimatedSizeBytes?: number;
  lastErrorMessage?: string | null;
  pendingOperations: SyncPendingOperation[];
  user: { uid?: string | null; email?: string | null };
  clinicName?: string;
  now?: Date;
}

function readEnv(key: string): string | undefined {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  return v && v.length > 0 ? v : undefined;
}

/**
 * Assemble the full diagnostic bundle. Project/org identifiers come from build
 * env only — they are NEVER taken from user-editable inputs (PRD §10).
 */
export function buildSyncNeedHelpBundle(params: BuildBundleParams): SyncNeedHelpBundle {
  const now = params.now ?? new Date();
  const createdAt = now.toISOString();

  return {
    caseId: params.caseId,
    createdAt,
    app: {
      name: 'web-admin',
      version: readEnv('VITE_APP_VERSION'),
      environment: readEnv('MODE') ?? import.meta.env.MODE,
    },
    user: {
      uid: params.user.uid ?? null,
      email: params.user.email ?? null,
    },
    project: {
      firebaseProjectId: readEnv('VITE_FIREBASE_PROJECT_ID'),
      orgSlug: readEnv('VITE_CLIENT_ID'),
      clinicName: params.clinicName,
    },
    sync: {
      state: params.state,
      lastSyncedAt: params.lastSyncedAt ?? null,
      pendingCount: params.pendingCount,
      estimatedSizeBytes: params.estimatedSizeBytes,
      writeUrl: readEnv('VITE_SYNCWRITE_URL'),
      lastErrorMessage: params.lastErrorMessage ?? null,
    },
    pendingOperations: params.pendingOperations,
    recentUploadErrors: getRecentUploadErrors(),
    retryLogs: getRetryLogs(),
    environment: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      platform: typeof navigator !== 'undefined' ? navigator.platform : undefined,
      language: typeof navigator !== 'undefined' ? navigator.language : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      currentUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    },
  };
}

/** Build the summary manifest (no operation values). */
export function buildManifest(bundle: SyncNeedHelpBundle): SyncNeedHelpManifest {
  return {
    caseId: bundle.caseId,
    createdAt: bundle.createdAt,
    appName: bundle.app.name,
    pendingCount: bundle.sync.pendingCount,
    firebaseProjectId: bundle.project.firebaseProjectId,
    orgSlug: bundle.project.orgSlug,
    userUid: bundle.user.uid ?? null,
    userEmail: bundle.user.email ?? null,
    lastErrorMessage: bundle.sync.lastErrorMessage ?? null,
  };
}

// ─── UTF-8-safe base64 ───

function utf8ToBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// ─── Submission ───

export interface SubmitOptions {
  needHelpUrl?: string;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
}

/**
 * Submit a Need Help case to the per-project `needHelp` Cloud Function.
 *
 * Throws on misconfiguration, auth failure, or non-2xx responses (the caller
 * surfaces the message to the user).
 */
export async function submitSyncNeedHelpCase(
  bundle: SyncNeedHelpBundle,
  opts: SubmitOptions,
): Promise<SubmitResult> {
  const url = opts.needHelpUrl ?? readEnv('VITE_NEED_HELP_URL');
  if (!url) {
    throw new Error('Support request is not configured for this app (missing VITE_NEED_HELP_URL).');
  }

  const token = await opts.getIdToken();
  if (!token) {
    throw new Error('You must be signed in to send a support request.');
  }

  const json = JSON.stringify(bundle);
  const request: SyncNeedHelpRequest = {
    caseId: bundle.caseId,
    manifest: buildManifest(bundle),
    bundleBase64: utf8ToBase64(json),
    bundleContentType: 'application/json',
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  const text = await res.text();
  if (!res.ok) {
    if (res.status === 413) {
      throw new Error('The diagnostic bundle is too large to send. Please contact support.');
    }
    throw new Error(`Support request failed (${res.status}): ${text || res.statusText}`);
  }

  let parsed: SubmitResult;
  try {
    parsed = JSON.parse(text) as SubmitResult;
  } catch {
    parsed = { ok: true, caseId: bundle.caseId };
  }
  return { ok: parsed.ok ?? true, caseId: parsed.caseId ?? bundle.caseId };
}

// ─── One-shot helper (shared by Sync Status page + stuck-sync modal) ───

export interface CreateNeedHelpCaseParams {
  db: AbstractPowerSyncDatabase;
  health: {
    state: SyncHealthState;
    lastSyncedAtIso?: string | null;
    pendingCount: number;
    estimatedSizeBytes?: number;
    lastErrorMessage?: string | null;
  };
  user: { uid?: string | null; email?: string | null };
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  clinicName?: string;
}

/**
 * Extract the pending queue, build the bundle, and submit it in one call.
 * Used by both the Sync Status page action and the global stuck-sync modal so
 * the bundle-creation logic lives in exactly one place.
 */
export async function createNeedHelpCase(params: CreateNeedHelpCaseParams): Promise<SubmitResult> {
  const pendingOperations = await extractPendingOperations(params.db);
  const caseId = generateCaseId();
  const bundle = buildSyncNeedHelpBundle({
    caseId,
    state: params.health.state,
    lastSyncedAt: params.health.lastSyncedAtIso,
    pendingCount: params.health.pendingCount,
    estimatedSizeBytes: params.health.estimatedSizeBytes,
    lastErrorMessage: params.health.lastErrorMessage,
    pendingOperations,
    user: params.user,
    clinicName: params.clinicName,
  });
  return submitSyncNeedHelpCase(bundle, { getIdToken: params.getIdToken });
}
